import { useCallback, useRef, useState } from "react";

interface DragState {
  key: string;
  startX: number;
  startWidth: number;
  minWidth: number;
}

export interface ColumnResizeHandleOptions {
  /** The narrowest the column can be dragged to. */
  minWidth: number;
  /**
   * The width to start the drag from when the column has not been resized yet.
   * Left out, the header cell the handle sits in is measured, so a table laid out
   * by the browser keeps its natural widths until a column is dragged.
   */
  startWidth?: number;
}

export interface ColumnResizeHandleProps {
  onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerMove: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerUp: (event: React.PointerEvent<HTMLElement>) => void;
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
}

/**
 * Drag-to-resize for table columns, shared by the page designer's RecordTable (as
 * used by TabbedTables) and the submit-backed form tables such as /search.
 *
 * Spread `getHandleProps(key, options)` onto an element at the right edge of each
 * resizable header cell. Widths are kept for as long as the table is mounted and
 * are not saved.
 */
export function useColumnResize(
  initialWidths: Record<string, number> = {}
) {
  const [widths, setWidths] =
    useState<Record<string, number>>(initialWidths);
  const dragState = useRef<DragState | null>(null);
  // Set once the pointer moves during a drag. If the drag ends somewhere other than
  // the handle, the browser can send the trailing click to the header cell, whose
  // click sorts - wasDragged() lets that click be ignored.
  const didDrag = useRef(false);

  const getHandleProps = useCallback(
    (
      key: string,
      { minWidth, startWidth }: ColumnResizeHandleOptions
    ): ColumnResizeHandleProps => ({
      onPointerDown: (event) => {
        event.stopPropagation();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        const measured =
          event.currentTarget.parentElement?.getBoundingClientRect()
            .width ?? 0;
        didDrag.current = false;
        dragState.current = {
          key,
          startX: event.clientX,
          startWidth: widths[key] ?? startWidth ?? measured,
          minWidth,
        };
      },
      onPointerMove: (event) => {
        if (!dragState.current || dragState.current.key !== key) {
          return;
        }
        didDrag.current = true;
        const current = dragState.current;
        const nextWidth = Math.max(
          current.minWidth,
          current.startWidth + (event.clientX - current.startX)
        );
        setWidths((previous) => ({ ...previous, [key]: nextWidth }));
      },
      onPointerUp: (event) => {
        event.currentTarget.releasePointerCapture?.(event.pointerId);
        dragState.current = null;
        // The click that follows pointerup is dispatched straight after it, so
        // clear the flag once that has had its chance to read it.
        setTimeout(() => {
          didDrag.current = false;
        }, 0);
      },
      onClick: (event) => event.stopPropagation(),
    }),
    [widths]
  );

  /** True when the click being handled is the tail end of a resize drag. */
  const wasDragged = useCallback(() => {
    if (didDrag.current) {
      didDrag.current = false;
      return true;
    }
    return false;
  }, []);

  return { getHandleProps, setWidths, wasDragged, widths };
}

export default useColumnResize;
